// Thin fetch wrapper for the JSON API. Session cookies are httpOnly (the browser sends them); the
// CSRF token (brief §2) is kept in memory and sent as X-CSRF-Token on every state-changing call.

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

interface ErrorBody {
  error?: string;
  code?: string;
}

export async function api<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined && !(body instanceof FormData))
    headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      ...(body === undefined
        ? {}
        : { body: body instanceof FormData ? body : JSON.stringify(body) }),
    });
  } catch {
    // The request never reached us: say so, instead of a generic error (LANE-D-22).
    throw new ApiError(
      0,
      'Couldn’t connect. Please check your internet connection and try again.',
      'NETWORK',
    );
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  if (!res.ok) {
    const err = (parsed ?? {}) as ErrorBody;
    throw new ApiError(
      res.status,
      err.error ?? 'Something went wrong. Please try again.',
      err.code,
      parsed,
    );
  }
  return parsed as T;
}

// Fire-and-forget beacon for billing facts (contact clicks) that must survive a navigation.
export function beacon(path: string, body: unknown): void {
  const payload = JSON.stringify(body);
  if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
    const ok = navigator.sendBeacon(path, new Blob([payload], { type: 'application/json' }));
    if (ok) return;
  }
  void fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
}
